import { formatText, type Label, type Locale } from "../texts";

// Reglas de Seguridad y acceso y de Uso y soporte de una empresa (R4), sin React: se prueban en security.test.tsx.

/** Vencimientos que se pueden elegir (§5.5); CUSTOM solo aparece si ya es el valor de la empresa, como dato. */
export const expirationOptions = ["NEVER", "30_DAYS", "60_DAYS", "90_DAYS", "180_DAYS"] as const;

/** Modos de la verificación en dos pasos, en el orden de la pantalla. */
export const mfaModes = ["DISABLED", "OPTIONAL", "REQUIRED_PRIVILEGED", "REQUIRED_ALL"] as const;

const expirationDays: Record<string, number> = { "30_DAYS": 30, "60_DAYS": 60, "90_DAYS": 90, "180_DAYS": 180 };

/** «Nunca», «Cada 90 días» o, para CUSTOM, «Personalizado (se conserva)». */
export function expirationLabel(L: Label, value: string): string {
  if (value === "NEVER") return L("ADM_SEC_EXP_NEVER");
  if (value === "CUSTOM") return L("ADM_SEC_EXP_CUSTOM");
  const days = expirationDays[value];
  return days ? formatText(L("ADM_SEC_EXP_DAYS"), days) : L("USAGE_UNKNOWN");
}

export const mfaKey: Record<string, { title: string; line: string }> = {
  DISABLED: { title: "ADM_SEC_MFA_DISABLED", line: "ADM_SEC_MFA_DISABLED_LINE" },
  OPTIONAL: { title: "ADM_SEC_MFA_OPTIONAL", line: "ADM_SEC_MFA_OPTIONAL_LINE" },
  REQUIRED_PRIVILEGED: { title: "ADM_SEC_MFA_ADMINS", line: "ADM_SEC_MFA_ADMINS_LINE" },
  REQUIRED_ALL: { title: "ADM_SEC_MFA_ALL", line: "ADM_SEC_MFA_ALL_LINE" },
};

/** Una frase con su número en singular o plural (E18). */
const counted = (L: Label, locale: Locale, count: number, one: string, many: string, ...rest: (string | number)[]) =>
  formatText(L(count === 1 ? one : many), count.toLocaleString(locale), ...rest);

/**
 * D9 · el texto de la confirmación `politica`: lo que cambia y a cuántas personas afecta, con su número. Sin cambios,
 * null (Guardar lo dice en un toast y no abre la ventana). `people` null: el conteo no respondió y la frase lo dice sin
 * número (nunca 0, H-3).
 */
export function policyImpact(
  L: Label,
  locale: Locale,
  before: { expiration: string; mfa: string },
  after: { expiration: string; mfa: string },
  people: { active: number; admins: number } | null,
): string | null {
  const parts: string[] = [];
  if (after.expiration !== before.expiration) {
    const days = expirationDays[after.expiration];
    if (!people) parts.push(days ? formatText(L("ADM_SEC_IMPACT_EXP_ANY"), days) : L("ADM_SEC_IMPACT_EXP_NEVER_ANY"));
    else if (days) parts.push(counted(L, locale, people.active, "ADM_SEC_IMPACT_EXP_ONE", "ADM_SEC_IMPACT_EXP", days));
    else parts.push(counted(L, locale, people.active, "ADM_SEC_IMPACT_EXP_NEVER_ONE", "ADM_SEC_IMPACT_EXP_NEVER"));
  }
  if (after.mfa !== before.mfa) {
    const key = {
      DISABLED: "ADM_SEC_IMPACT_MFA_OFF",
      OPTIONAL: "ADM_SEC_IMPACT_MFA_OPTIONAL",
      REQUIRED_PRIVILEGED: "ADM_SEC_IMPACT_MFA_ADMINS",
      REQUIRED_ALL: "ADM_SEC_IMPACT_MFA_ALL",
    }[after.mfa];
    if (key) {
      const count = after.mfa === "REQUIRED_PRIVILEGED" ? people?.admins : people?.active;
      parts.push(count == null ? L(`${key}_ANY`) : counted(L, locale, count, `${key}_ONE`, key));
    }
  }
  return parts.length ? parts.join(" ") : null;
}

/** Quién hizo algo: «Bheli» para el sistema, «Nombre · Platform Admin» para soporte, o la persona. */
export function actorText(L: Label, actor: string | null, context: string): string {
  if (context === "SYSTEM" || !actor) return L("ADM_PPL_ACTOR_SYSTEM");
  return context === "PLATFORM_ADMIN" ? formatText(L("ADM_PPL_ACTOR_PLATFORM"), actor) : actor;
}

/**
 * Qué pasó en la auditoría, con frases del negocio (D7: nunca el código ni el JSON). Una acción nueva dice «Hizo un
 * cambio de seguridad». Un módulo de la empresa se nombra con su nombre.
 */
export function auditText(L: Label, kind: string, moduleName?: string | null): string {
  const text = L(`ADM_AUD_EVT_${kind}`);
  if (text.startsWith("⟦")) return L("ADM_AUD_EVT_OTHER");
  return formatText(text, moduleName ?? L("ADM_AUD_A_MODULE"));
}

/** Nombre de un dispositivo: «Chrome en Windows» con navegador y sistema; sin ellos, la etiqueta que guardó Identity. */
export function deviceName(
  L: Label,
  session: { device: string | null; browser: string | null; os: string | null },
): string {
  // Identity guarda «UNKNOWN» cuando no reconoce el navegador o el sistema: nunca se muestra (E40).
  const known = (value: string | null) => (value && value !== "UNKNOWN" ? value : null);
  const browser = known(session.browser);
  const os = known(session.os);
  if (browser && os) return formatText(L("ADM_PPL_DEVICE"), browser, os);
  // La etiqueta «(127.0.0.1)» (solo la IP) nunca es el nombre: la IP va en su columna (como hoy en el Platform Admin).
  if (session.device && !session.device.includes("UNKNOWN") && !/^\(.*\)$/.test(session.device.trim()))
    return session.device;
  return browser ?? os ?? L("ADM_PPL_DEVICE_UNKNOWN");
}

/** Correo con forma de correo (la búsqueda de Recuperar acceso no sale al servidor con otra cosa). */
export const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
